import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { useDroplet } from '../context/DropletContext';

export function ForgotPassword() {
  const { authApi } = useDroplet();
  const [username, setUsername] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // The backend always returns the same generic response regardless of
      // whether the account exists — nothing here to branch on either way.
      await authApi.forgotPassword(username);
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-dark-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.png" alt="3pplea Console" className="h-12 w-auto mb-3" />
          <h1 className="text-xl font-bold text-white">3pplea Console</h1>
          <p className="text-sm text-dark-400 mt-1">Reset your password</p>
        </div>

        <div className="bg-dark-900 border border-dark-700 rounded-xl p-6 space-y-4">
          {submitted ? (
            <div className="text-center space-y-4">
              <div className="bg-dark-800 border border-dark-700 text-dark-200 text-sm rounded-lg px-3 py-3">
                If that account exists, a reset link has been sent to its email address. The
                link expires in 30 minutes.
              </div>
              <Link
                to="/"
                className="inline-flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-dark-400">
                Enter your username or account email. If it matches an admin account, we'll
                send a reset link to the email on file.
              </p>

              <div>
                <label htmlFor="username" className="block text-xs text-dark-400 mb-1">
                  Username or email
                </label>
                <input
                  id="username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="w-full bg-dark-800 border border-dark-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-600"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white font-medium rounded-lg px-4 py-2 text-sm transition-colors"
              >
                <Mail className="h-4 w-4" />
                {submitting ? 'Sending…' : 'Send reset link'}
              </button>

              <Link
                to="/"
                className="flex items-center justify-center gap-1 text-sm text-dark-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
